import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faHeading,
  faListUl,
  faListOl,
  faBold,
  faItalic,
  faCode,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { renderMarkdown } from "./markdown/renderMarkdown";

const FullTextEditPopup = ({ text, setText, onCancel, onSubmit }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit(e);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const wrapSelection = (before, after = before, placeholder = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = text.slice(start, end) || placeholder;
    const next =
      text.slice(0, start) + before + selected + after + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursorStart = start + before.length;
      textarea.setSelectionRange(cursorStart, cursorStart + selected.length);
    });
  };

  const prefixLines = (prefix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const region = text.slice(lineStart, end);
    const prefixed = region
      .split("\n")
      .map((line, idx) => {
        if (/^\d+\. /.test(prefix)) return `${idx + 1}. ${line}`;
        return prefix + line;
      })
      .join("\n");
    const next = text.slice(0, lineStart) + prefixed + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(lineStart, lineStart + prefixed.length);
    });
  };

  const charCount = text?.length || 0;
  const lineCount = text ? text.split("\n").length : 1;

  const toolbarBtn =
    "p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-primary";

  return createPortal(
    <div
      className="fixed z-50 inset-0 flex items-stretch justify-center p-6 sm:p-10"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        className="absolute inset-0 bg-gray-900 bg-opacity-60 focus:outline-none cursor-default"
        onClick={() => onCancel()}
      />
      <form
        onSubmit={onSubmit}
        className="relative flex flex-col flex-1 min-h-0 bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        <header className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <h3
              id="modal-title"
              className="text-base font-semibold text-gray-800"
            >
              Focus editor
            </h3>
            <span className="text-xs text-gray-400">
              {lineCount} {lineCount === 1 ? "line" : "lines"} · {charCount}{" "}
              {charCount === 1 ? "char" : "chars"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-3 text-xs text-gray-500 mr-2">
              <span>
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono text-[10px]">
                  ⌘/Ctrl
                </kbd>{" "}
                +{" "}
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono text-[10px]">
                  Enter
                </kbd>{" "}
                save
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono text-[10px]">
                  Esc
                </kbd>{" "}
                cancel
              </span>
            </div>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => onCancel()}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Save
            </button>
            <button
              type="button"
              aria-label="Close"
              className="ml-1 p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={() => onCancel()}
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row bg-gray-50">
          <div className="flex flex-col flex-1 min-h-0 md:border-r md:border-gray-200">
            <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-200 bg-white">
              <button
                type="button"
                title="Heading 1"
                aria-label="Heading 1"
                className={toolbarBtn}
                onClick={() => prefixLines("# ")}
              >
                <FontAwesomeIcon icon={faHeading} className="w-3.5 h-3.5" />
                <span className="text-[10px] align-super ml-0.5">1</span>
              </button>
              <button
                type="button"
                title="Heading 2"
                aria-label="Heading 2"
                className={toolbarBtn}
                onClick={() => prefixLines("## ")}
              >
                <FontAwesomeIcon icon={faHeading} className="w-3 h-3" />
                <span className="text-[10px] align-super ml-0.5">2</span>
              </button>
              <button
                type="button"
                title="Heading 3"
                aria-label="Heading 3"
                className={toolbarBtn}
                onClick={() => prefixLines("### ")}
              >
                <FontAwesomeIcon icon={faHeading} className="w-2.5 h-2.5" />
                <span className="text-[10px] align-super ml-0.5">3</span>
              </button>
              <span className="w-px h-5 bg-gray-200 mx-1" />
              <button
                type="button"
                title="Bold"
                aria-label="Bold"
                className={toolbarBtn}
                onClick={() => wrapSelection("**", "**", "bold")}
              >
                <FontAwesomeIcon icon={faBold} className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                title="Italic"
                aria-label="Italic"
                className={toolbarBtn}
                onClick={() => wrapSelection("*", "*", "italic")}
              >
                <FontAwesomeIcon icon={faItalic} className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                title="Inline code"
                aria-label="Inline code"
                className={toolbarBtn}
                onClick={() => wrapSelection("`", "`", "code")}
              >
                <FontAwesomeIcon icon={faCode} className="w-3.5 h-3.5" />
              </button>
              <span className="w-px h-5 bg-gray-200 mx-1" />
              <button
                type="button"
                title="Bulleted list"
                aria-label="Bulleted list"
                className={toolbarBtn}
                onClick={() => prefixLines("- ")}
              >
                <FontAwesomeIcon icon={faListUl} className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                title="Numbered list"
                aria-label="Numbered list"
                className={toolbarBtn}
                onClick={() => prefixLines("1. ")}
              >
                <FontAwesomeIcon icon={faListOl} className="w-3.5 h-3.5" />
              </button>
              <span className="w-px h-5 bg-gray-200 mx-1" />
              <button
                type="button"
                title="Link"
                aria-label="Link"
                className={toolbarBtn}
                onClick={() => wrapSelection("[", "](url)", "text")}
              >
                <FontAwesomeIcon icon={faLink} className="w-3.5 h-3.5" />
              </button>
            </div>
            <textarea
              ref={textareaRef}
              value={text}
              placeholder="Write markdown here..."
              className="block flex-1 w-full px-5 py-4 text-sm text-gray-800 bg-white resize-none focus:outline-none font-mono leading-relaxed"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="flex flex-col flex-1 min-h-0 bg-white">
            <div className="px-4 py-2 border-b border-gray-200 text-xs font-medium uppercase tracking-wide text-gray-500">
              Preview
            </div>
            <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
              {text ? (
                <div className="markdown-preview">{renderMarkdown(text)}</div>
              ) : (
                <div className="text-sm text-gray-400 italic">
                  Preview will appear here
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default FullTextEditPopup;
